/**
 * 메인 우상단 "내 위젯" — 로그인/비로그인 분기.
 *
 * - 로그인: 환영 인사 + 안읽은 알림 수 + 북마크/돌봄일지 빠른 진입
 * - 비로그인: 가입 유도 카드 (로그인/회원가입 버튼)
 *
 * 영구지침: docs/plan/development-roadmap.md (메인 포털화 1차)
 */
import { Link } from 'react-router-dom'
import { Bell, Heart, NotebookPen, ChevronRight, LogIn, UserPlus } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useHasMounted } from '@/hooks/useHasMounted'
import { useUnreadCount } from '@/features/notification/api'
import { ROUTES } from '@/lib/constants'

export default function MyWidget() {
  const hasMounted = useHasMounted()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const { data: unreadCount } = useUnreadCount()

  // SSR/하이드레이션: 로그인 정보가 확정되기 전엔 비로그인 상태로 그림
  const showAuthed = hasMounted && isAuthenticated && user

  return (
    <aside className="flex h-full flex-col rounded-2xl border border-cb-gray-100 bg-white p-5">
      {showAuthed ? <AuthedView name={user.name} unread={unreadCount ?? 0} /> : <GuestView />}
    </aside>
  )
}

function AuthedView({ name, unread }: { name: string; unread: number }) {
  return (
    <>
      <div className="mb-4">
        <p className="text-caption text-cb-gray-500">반갑습니다,</p>
        <p className="text-lg font-bold text-cb-gray-900">{name}님</p>
      </div>

      <Link
        to="/mypage/notifications"
        className="mb-2 flex items-center gap-2 rounded-xl bg-cb-gray-50 px-3 py-3 hover:bg-cb-primary-light"
      >
        <Bell size={18} className="text-cb-primary" />
        <span className="text-sm font-medium text-cb-gray-900">알림</span>
        {unread > 0 && (
          <span className="ml-auto rounded-full bg-cb-danger px-2 py-0.5 text-[11px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
        {unread === 0 && <ChevronRight size={14} className="ml-auto text-cb-gray-400" />}
      </Link>

      <Link
        to="/bookmarks"
        className="mb-2 flex items-center gap-2 rounded-xl bg-cb-gray-50 px-3 py-3 hover:bg-cb-primary-light"
      >
        <Heart size={18} className="text-cb-primary" />
        <span className="text-sm font-medium text-cb-gray-900">관심 시설</span>
        <ChevronRight size={14} className="ml-auto text-cb-gray-400" />
      </Link>

      <Link
        to={ROUTES.CARE_LOG}
        className="mt-auto flex items-center justify-center gap-2 rounded-xl bg-cb-primary px-3 py-3 text-sm font-bold text-white hover:bg-cb-primary-hover"
      >
        <NotebookPen size={16} />
        오늘의 돌봄일지 쓰기
      </Link>
    </>
  )
}

function GuestView() {
  return (
    <>
      <div className="mb-4">
        <p className="text-lg font-bold text-cb-gray-900">CareBalance와</p>
        <p className="text-lg font-bold text-cb-primary">함께 시작하세요</p>
        <p className="mt-2 text-sm text-cb-gray-500">
          로그인하면 관심 시설, 돌봄일지, 맞춤 추천을 이용할 수 있어요.
        </p>
      </div>

      <Link
        to={ROUTES.LOGIN}
        className="mb-2 flex items-center justify-center gap-2 rounded-xl bg-cb-primary px-3 py-3 text-sm font-bold text-white hover:bg-cb-primary-hover"
      >
        <LogIn size={16} />
        로그인
      </Link>
      <Link
        to={ROUTES.SIGNUP}
        className="flex items-center justify-center gap-2 rounded-xl border border-cb-gray-200 px-3 py-3 text-sm font-bold text-cb-gray-900 hover:bg-cb-gray-50"
      >
        <UserPlus size={16} />
        회원가입
      </Link>

      <Link
        to={ROUTES.RECOMMEND}
        className="mt-auto pt-4 text-center text-xs text-cb-gray-500 hover:text-cb-primary hover:underline"
      >
        비회원으로 둘러보기 →
      </Link>
    </>
  )
}
